import React, { useState, useEffect } from 'react';
import { FaMapMarkerAlt, FaWhatsapp, FaClock, FaEnvelope, FaPhone } from 'react-icons/fa';
import { motion } from 'framer-motion';
import AppUrl from '../../restApi/AppUrl';
import IRestClient from '../../restApi/IRestClient';
import './Contact.css';

const vp = { once: true, amount: 0.2 };

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: (d = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, delay: d, ease: [0.22, 1, 0.36, 1] },
  }),
};

const slideLeft = {
  hidden: { opacity: 0, x: -36 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] } },
};

const slideRight = {
  hidden: { opacity: 0, x: 36 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.65, ease: [0.22, 1, 0.36, 1] } },
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.09, delayChildren: 0.15 } },
};

const itemFade = {
  hidden: { opacity: 0, y: 14 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.42, ease: 'easeOut' } },
};

function Contact() {
  const [contactInfo, setContactInfo] = useState({
    address: '',
    officeHours: 'Monday – Friday: 9:00 AM – 5:00 PM',
    ownerEmail: '',
    phone: '',
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    IRestClient.GetRequest(AppUrl.Footer)
      .then(result => {
        if (result && result.length > 0) {
          const d = result[0];
          setContactInfo(prev => ({
            ...prev,
            address: d.adress || 'Address not available',
            ownerEmail: d.email || 'Email not available',
            phone: d.number || 'Phone not available',
          }));
        } else {
          setError(true);
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        setError(true);
      });
  }, []);

  const handleWhatsapp = () => {
    const msg = encodeURIComponent(
      "Hello SOYAN Initiative! 👋\n\nI visited your website and I'd like to learn more about your work supporting refugees and communities in Uganda.\n\nCould you please guide me on how I can get involved or support your mission?\n\nThank you!"
    );
    window.open(`https://wa.me/256756897924?text=${msg}`, '_blank');
  };

  const handleEmail = () => {
    const subject = encodeURIComponent('Inquiry – SOYAN Initiative');
    const body = encodeURIComponent(
      'Hello SOYAN Initiative,\n\nI visited your website and would like to learn more about your programs supporting refugees and communities.\n\nKind regards,'
    );
    window.open(`mailto:${contactInfo.ownerEmail}?subject=${subject}&body=${body}`, '_blank');
  };

  const infoItems = [
    { icon: <FaMapMarkerAlt />, label: 'Location',     value: contactInfo.address },
    { icon: <FaClock />,        label: 'Office Hours', value: contactInfo.officeHours },
    { icon: <FaPhone />,        label: 'Phone',        value: contactInfo.phone },
    { icon: <FaEnvelope />,     label: 'Email',        value: contactInfo.ownerEmail },
  ];

  if (loading) {
    return (
      <section className="contact-section">
        <div className="contact-container">

          {/* Header skeleton */}
          <div className="contact-header">
            <div className="ct-skel h-6 w-32 mx-auto mb-4 rounded-full" style={{ height: 24, width: 128, margin: '0 auto 16px', borderRadius: 20 }} />
            <div className="ct-skel h-9 w-64 mx-auto mb-3 rounded-lg" style={{ height: 36, width: 256, margin: '0 auto 12px', borderRadius: 8 }} />
            <div className="ct-skel h-3.5 w-72 mx-auto rounded" style={{ height: 14, width: 288, margin: '0 auto', borderRadius: 4 }} />
          </div>

          {/* Grid skeleton */}
          <div className="contact-grid">

            {/* Map skeleton */}
            <div className="ct-skel contact-map-wrapper" style={{ borderRadius: 16 }} />

            {/* Right panel skeleton */}
            <div className="contact-right">

              {/* Info list skeleton */}
              <div style={{ background: '#fff', borderRadius: 14, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
                {[1, 2, 3, 4].map(i => (
                  <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '15px 20px', borderBottom: i < 4 ? '1px solid #f3f4f6' : 'none' }}>
                    <div className="ct-skel" style={{ width: 34, height: 34, minWidth: 34, borderRadius: 9 }} />
                    <div style={{ flex: 1 }}>
                      <div className="ct-skel" style={{ height: 10, width: 60, borderRadius: 4, marginBottom: 8 }} />
                      <div className="ct-skel" style={{ height: 13, width: '75%', borderRadius: 4 }} />
                    </div>
                  </div>
                ))}
              </div>

              {/* CTA skeleton */}
              <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 14, padding: '22px 24px' }}>
                <div className="ct-skel" style={{ height: 10, width: 90, borderRadius: 4, marginBottom: 10 }} />
                <div className="ct-skel" style={{ height: 13, width: '80%', borderRadius: 4, marginBottom: 6 }} />
                <div className="ct-skel" style={{ height: 13, width: '60%', borderRadius: 4, marginBottom: 18 }} />
                <div style={{ display: 'flex', gap: 10 }}>
                  <div className="ct-skel" style={{ height: 36, width: 110, borderRadius: 8 }} />
                  <div className="ct-skel" style={{ height: 36, width: 100, borderRadius: 8 }} />
                </div>
              </div>

            </div>
          </div>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="contact-section">
        <div className="contact-error">
          Unable to load contact details. Please try again later.
        </div>
      </section>
    );
  }

  return (
    <section className="contact-section">
      <div className="contact-container">

        {/* ── Header ── */}
        <motion.div
          className="contact-header"
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
        >
          <span className="contact-badge">
            <FaEnvelope style={{ marginRight: 6, fontSize: 11 }} />
            Get In Touch
          </span>
          <h2 className="contact-title">
            Connect <span className="contact-title-accent">With Us</span>
          </h2>
          <p className="contact-subtitle">
            We're here to help and answer any questions. Reach out through any of our channels below.
          </p>
        </motion.div>

        {/* ── Main grid ── */}
        <div className="contact-grid">

          {/* Map */}
          <motion.div
            className="contact-map-wrapper"
            variants={slideLeft}
            initial="hidden"
            whileInView="visible"
            viewport={vp}
          >
            <div className="contact-map-accent" />
            <iframe
              title="Our Location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3022.215573144536!2d-73.9878449242398!3d40.74844097138992!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89c259a9b3117469%3A0xd134e199a405a163!2sEmpire%20State%20Building!5e0!3m2!1sen!2sus!4v1620000000000!5m2!1sen!2sus"
              style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
              allowFullScreen=""
              loading="lazy"
            />
          </motion.div>

          {/* Right panel */}
          <motion.div
            className="contact-right"
            variants={slideRight}
            initial="hidden"
            whileInView="visible"
            viewport={vp}
          >

            {/* Info list */}
            <motion.ul
              className="contact-info-list"
              variants={stagger}
              initial="hidden"
              whileInView="visible"
              viewport={vp}
            >
              {infoItems.map(({ icon, label, value }) => (
                <motion.li
                  key={label}
                  className="contact-info-item"
                  variants={itemFade}
                  whileHover={{ x: 4, transition: { type: 'spring', stiffness: 350, damping: 25 } }}
                >
                  <div className="contact-info-icon">{icon}</div>
                  <div>
                    <span className="contact-info-label">{label}</span>
                    <span className="contact-info-value">{value}</span>
                  </div>
                </motion.li>
              ))}
            </motion.ul>

            {/* CTA */}
            <motion.div
              className="contact-cta"
              variants={fadeUp}
              custom={0.25}
              initial="hidden"
              whileInView="visible"
              viewport={vp}
            >
              <span className="contact-cta-label">Direct Message</span>
              <p className="contact-cta-text">
                Reach out to our team directly via WhatsApp or email for faster responses.
              </p>
              <div className="contact-cta-buttons">
                <motion.button
                  className="contact-btn contact-btn-whatsapp"
                  onClick={handleWhatsapp}
                  whileHover={{ scale: 1.04, y: -2 }}
                  whileTap={{ scale: 0.97 }}
                >
                  <FaWhatsapp style={{ marginRight: 7, fontSize: 15 }} />
                  WhatsApp
                </motion.button>
                <motion.button
                  className="contact-btn contact-btn-email"
                  onClick={handleEmail}
                  whileHover={{ scale: 1.04, y: -2 }}
                  whileTap={{ scale: 0.97 }}
                >
                  <FaEnvelope style={{ marginRight: 7, fontSize: 13 }} />
                  Email Us
                </motion.button>
              </div>
            </motion.div>

          </motion.div>
        </div>

      </div>
    </section>
  );
}

export default Contact;
